import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { isEqual, cloneDeep, uniqWith } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { FilterPanelItem, FilterPanelOperator, SavedFilter } from './filter-panel.model';
import { IKuiSelectOption } from 'app/key-ui';
import { UserResponse } from '@key-telematics/fleet-api-client';
import { AppService } from 'app/app.service';

export interface FilterPanelFilter {
    date?: { start: string, end: string };
    page?: number;
    limit?: number;
    filters?: {
        [key: string]: FilterPanelItem[]
    };
    operators?: {
        [key: string]: FilterPanelOperator
    }
}

@Injectable()
export class FilterPanelService {

    filters: FilterPanelFilter = {
        filters: {},
    };

    private filtersSubject: BehaviorSubject<FilterPanelFilter> = new BehaviorSubject(this.filters);
    private filtersSignal: WritableSignal<FilterPanelFilter> = signal(this.filters);
    get filters$(): Observable<FilterPanelFilter> {
        return this.filtersSubject.asObservable();
    }

    private totalSubject: BehaviorSubject<number> = new BehaviorSubject(0);
    get total$(): Observable<number> {
        return this.totalSubject.asObservable();
    }

    // 'savedFiltersLoaded' is for anayltics purposes
    private savedFiltersLoaded: WritableSignal<boolean> = signal(false);
    readonly savedFiltersLoadedResult: Signal<boolean> = this.savedFiltersLoaded.asReadonly();

    // full saved filters details
    private savedFiltersState: WritableSignal<{ filters: SavedFilter[], selectedId: string | null }> = signal({ filters: [], selectedId: null });

    // computed values have 'result' on the end helps to distinguish them
    allSavedFiltersResult: Signal<SavedFilter[]> = computed(() => this.savedFiltersState().filters);
    selectedSavedFilterIdResult: Signal<string | null> = computed(() => this.savedFiltersState().selectedId);
    selectedSavedFilterResult: Signal<SavedFilter> = computed(() => this.savedFiltersState().filters.find(f => f.id === this.selectedSavedFilterIdResult()));

    hasActiveFiltersResult: Signal<boolean> = computed(() => Object.values(this.filtersSignal().filters || {}).some(v => v.length > 0));

    savedFiltersDropdownListResult: Signal<IKuiSelectOption[]> = computed(() => {
        const { filters, selectedId } = this.savedFiltersState();

        // hasActiveFiltersResult() is added below so when 'x' is clicked clearing filters from the 'bullet list' in the UI, the dropdown menu updates to 'All'
        const allOption: IKuiSelectOption = { id: undefined, value: 'All', selected: !selectedId || !this.hasActiveFiltersResult() };
        return [allOption, ...filters.map(f => ({ id: f.id, value: f.name, selected: f.id === selectedId && this.hasActiveFiltersResult() }))];
    });

    private readonly app = inject(AppService);

    user: WritableSignal<UserResponse> = signal(undefined);

    // for analytics purposes
    setSavedFiltersLoaded(value: boolean) {
        if (this.filters.filters && Object.keys(this.filters.filters).length > 0) {
		    this.savedFiltersLoaded.set(value);
        }
	}

    private emit(filters: FilterPanelFilter, forceEmit = false) {
        if (forceEmit || !isEqual(filters, this.filters)) {
            this.filters = filters;
            this.filtersSignal.set(filters);
            this.filtersSubject.next(cloneDeep(this.filters));
        }
    }

    getSavedFilters(user: UserResponse) {
        this.user.set(user);
        const userFlags: string = this.user()?.flags?.['fleet-ui-v2']?.['page-overview-filters'];
        const rawFilters = userFlags ? userFlags.split(';') : null;

        if (rawFilters?.length) {
            const filters = rawFilters.map((f, i) => {
                const [name, rest] = f.split(':eq(');
                const parts = rest.replace(')', '').split(',');
                const filtersMap: { [key: string]: string[] } = {};
                let currentKey: string;
                parts.forEach(p => {
                    if (!p.includes('-')) {
                        currentKey = p;
                        filtersMap[currentKey] = [];
                    } else {
                        filtersMap[currentKey].push(p);
                    }
                });
                return { id: String(i), name, filters: filtersMap } as SavedFilter;
            });
            this.updateSavedFilters(filters);
            this.setSavedFiltersLoaded(true);
        }
    }
    
    updateSavedFilters(filters: SavedFilter[]) {
        this.savedFiltersState.update(s => ({ ...s, filters }));
    }

    setSelectedSavedFilterId(id: string | undefined) {
        this.savedFiltersState.update(s => ({ ...s, selectedId: id }));
    }

    async saveFilter(name: string, isNewFilter: boolean): Promise<UserResponse> {
        const currentSavedFilters = this.allSavedFiltersResult();
        const newId = isNewFilter ? String(this.allSavedFiltersResult().length) : undefined;
        const filterBeingSaved: SavedFilter = {
            id: isNewFilter ? newId : this.selectedSavedFilterIdResult(),
            name,
            filters: Object.fromEntries(
                Object.entries(this.filters.filters || {}).map(([key, items]) => [key, items.map(i => i.id)])
            ),
        };

        const updatedSavedFilters = isNewFilter
            ? [...currentSavedFilters, filterBeingSaved]
            : currentSavedFilters.map(f => f.id === filterBeingSaved.id ? filterBeingSaved : f);

        this.updateSavedFilters(updatedSavedFilters);
        return this.app.api.accounts.updateUser(this.user().id, this.createSavedFilterPayload());
    }

    async deleteSavedFilter(): Promise<UserResponse> {
        const currentSavedFilters = this.allSavedFiltersResult();
        const removedSelectedFilters = currentSavedFilters.filter(f => f.id !== this.selectedSavedFilterIdResult());
        this.updateSavedFilters(removedSelectedFilters);
        return this.app.api.accounts.updateUser(this.user().id, this.createSavedFilterPayload());
    }

    createSavedFilterPayload() {
        const result = this.savedFiltersState().filters.map(item => {
            const filters = Object.entries(item.filters)
                .filter(([_, values]) => values.length > 0)
                .map(([key, values]) => `${key},${values.join(',')}`)
                .join(',');

            return `${item.name}:eq(${filters})`;
        });

        return {
            flags: {
                ...this.user().flags,
                'fleet-ui-v2': {
                    ...(this.user().flags?.['fleet-ui-v2'] || {}),
                    'page-overview-filters': result.join(';')
                }
            }
        };
    }

    update(values: FilterPanelFilter, forceEmit = false) {
        this.emit({
            ...this.filters,
            ...values,
            page: values.page || 1, // reset the page to 1 if anything other than page is set
        }, forceEmit);
    }

    updateFilterValues(key: string, values: FilterPanelItem[]) {
        this.emit({
            ...this.filters,
            page: 1,
            filters: {
                ...this.filters.filters,
                [key]: uniqWith([...values], isEqual),
            },
        });
    }

    /** this won't replace existing filters, just add to them */
    addFilterValues(key: string, values: FilterPanelItem[]) {
        this.emit({
            ...this.filters,
            page: 1,
            filters: {
                ...this.filters.filters,
                [key]: uniqWith([...this.filters.filters[key] || [], ...values], isEqual),
            },
        });
    }

    resetFilters() {
        this.emit({
            ...this.filters,
            filters: {},
        });
    }

    updateTotal(total: number) {
        this.totalSubject.next(total);
    }

    updateOperator(key: string, operator: FilterPanelOperator): void {
        this.emit({
            ...this.filters,
            operators: {
                ...this.filters.operators,
                [key]: operator
            }
        });
    }

}
